import { Avatar, Button, Card, Checkbox, Descriptions, Empty, Pagination, Popover, Spin, Table, Tooltip } from 'antd'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import usePermissions from '../../hooks/usePermissions';

const calculateTimeElapsed = (accepted_on, created_at) => {

  if (!created_at) return "-"; // If `created_at` is missing, return "-"
  let result;

  if (!accepted_on) {
    // If `accepted_on` is null, calculate time from `created_at` to now
    const createdDate = moment(created_at);
    const now = moment();
    const duration = moment.duration(now.diff(createdDate));

    const hours = String(Math.floor(duration.asHours()) % 24).padStart(2, "0");
    const minutes = String(duration.minutes()).padStart(2, "0");
    const seconds = String(duration.seconds()).padStart(2, "0");

    result = `${hours}:${minutes}:${seconds}`;
  } else {
    // If `accepted_on` is present, calculate time from `created_at` to `accepted_on`
    const acceptedDate = moment(accepted_on, "DD-MM-YYYY hh:mm:ss");
    const createdDate = moment(created_at, "DD-MM-YYYY hh:mm:ss");
    const duration = moment.duration(acceptedDate.diff(createdDate));

    const days = Math.floor(duration.asDays());
    const hours = String(Math.floor(duration.asHours()) % 24).padStart(2, "0");
    const minutes = String(duration.minutes()).padStart(2, "0");
    const seconds = String(duration.seconds()).padStart(2, "0");

    result = `${hours}:${minutes}:${seconds}`;
  }

  return result;
};

const MobileCompAllocation = ({
  data,
  pagination,
  setPagination,
  loading,
  selectedUids,
  setSelectedUids,
}) => {


  const [timeElapsedArray, setTimeElapsedArray] = useState([]);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    const interval = setInterval(() => {
      const updatedElapsedTimes = data.map((record) =>
        calculateTimeElapsed(record.accepted_on, record.created_at)
      );
      setTimeElapsedArray(updatedElapsedTimes);
    }, 1000); // Updates every second

    return () => clearInterval(interval);
  }, [data]);

  const onCheckboxChange = (key) => {
    setSelectedUids((prevUids) =>
      prevUids.includes(key)
        ? prevUids.filter((id) => id !== key)
        : [...prevUids, key]
    );
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    const allUids = data.map((item) => item.key);
    setSelectedUids(isChecked ? allUids : []);
  };

  return (
    <div style={{ marginBottom: "20px" }}>
      {loading ? (
        <Spin />
      ) : (
        <>
          {data.length > 0 ? (
            <>
              {hasPermission("can_clear_accept_reject_priority") && (
                <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center", gap: 8 }}>
                  <Checkbox
                    indeterminate={
                      selectedUids.length > 0 && selectedUids.length < data.length
                    }
                    checked={selectedUids.length === data.length}
                    onChange={handleSelectAll}
                  />
                  <span>Select All</span>
                </div>
              )}

              {data.map((allocationValue, index) => (
                <div key={index} style={{ marginBottom: "1rem" }}>
                  <Descriptions bordered size="small">
                    <Descriptions.Item label="Key">
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {hasPermission("can_clear_accept_reject_priority") && (
                          <Checkbox
                            checked={selectedUids.includes(allocationValue.key)}
                            onChange={() => onCheckboxChange(allocationValue.key)}
                          />
                        )}
                        <Link
                          target="_blank"
                          to={`/order-management/my-orders/${allocationValue.key}`}
                        >
                          {allocationValue.key || "-"}
                        </Link>
                      </div>
                    </Descriptions.Item>

                    <Descriptions.Item label="Title" style={{ maxWidth: "200px" }}>
                      {allocationValue.title || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Word Count">
                      {allocationValue.word_count || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Source">
                      {allocationValue.source || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Start Date">
                      {allocationValue.start_date
                        ? moment(allocationValue.start_date).local().format("DD-MM-YYYY hh:mm A")
                        : "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Alloted Deadline">
                      {allocationValue.allotted_deadline
                        ? moment(allocationValue.allotted_deadline).local().format("DD-MM-YYYY hh:mm A")
                        : "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Client Deadline">
                      {allocationValue.client_deadline
                        ? moment(allocationValue.client_deadline).local().format("DD-MM-YYYY hh:mm A")
                        : "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Accepted By">
                      {allocationValue.accepted_by || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Accepted On">
                      {allocationValue.accepted_on
                        ? moment(allocationValue.accepted_on).local().format("DD-MM-YYYY hh:mm A")
                        : "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Pending On">
                      {Array.isArray(allocationValue?.pending) && allocationValue.pending.length > 0 ? (
                        <Avatar.Group max={{ count: 3 }}>
                          {allocationValue.pending.slice(0, 2).map((name, idx) => (
                            <Tooltip key={idx} title={name}>
                              <Avatar style={{ backgroundColor: "#1890ff", color: "#fff" }}>
                                {name.charAt(0).toUpperCase()}
                              </Avatar>
                            </Tooltip>
                          ))}
                          {allocationValue.pending.length > 2 && (
                            <Popover
                              content={
                                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                                  {allocationValue.pending.slice(2).map((name, idx) => (
                                    <p key={idx}>{name}</p>
                                  ))}
                                </div>
                              }
                              title="More Pending"
                              placement='left'
                            >
                              <Avatar style={{ backgroundColor: "#f56a00" }}>
                                +{allocationValue.pending.length - 2}
                              </Avatar>
                            </Popover>
                          )}
                        </Avatar.Group>
                      ) : (
                        "-"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Rejections By">
                      <Popover
                        title={
                          <div style={{ maxHeight: "300px", overflowY: "auto", padding: 0 }}>
                            {allocationValue?.rejection_reasons?.map((reason, idx) => (
                              <Card key={idx} style={{ marginBottom: 8 }} bodyStyle={{ padding: 5 }}>
                                <p><strong>By:</strong> {reason.rejected_by}</p>
                                <p><strong>Reason:</strong> {reason.rejected_reason}</p>
                                <p>
                                  <strong>Rejected on:</strong>{" "}
                                  {moment(reason.rejected_on).format("DD MM YYYY h:mm A")}
                                </p>
                              </Card>
                            ))}
                          </div>
                        }
                      >
                        {allocationValue?.rejection_reasons?.length > 0 ? (
                          <Button type="link" style={{ padding: 0 }}>
                            View
                          </Button>
                        ) : (
                          "-"
                        )}
                      </Popover>
                    </Descriptions.Item>

                    <Descriptions.Item label="Visibility">
                      {Array.isArray(allocationValue?.visiblity) && allocationValue.visiblity.length > 0 ? (
                        <Avatar.Group max={{ count: 3 }}>
                          {allocationValue.visiblity.slice(0, 2).map((owner, idx) => (
                            <Tooltip key={idx} title={owner}>
                              <Avatar style={{ backgroundColor: "#87d068", color: "#fff" }}>
                                {owner.charAt(0).toUpperCase()}
                              </Avatar>
                            </Tooltip>
                          ))}
                          {allocationValue.visiblity.length > 2 && (
                            <Popover
                              content={
                                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                                  {allocationValue.visiblity.slice(2).map((owner, idx) => (
                                    <p key={idx}>{owner}</p>
                                  ))}
                                </div>
                              }
                              title="More Visibility"
                              placement="left"
                            >
                              <Avatar style={{ backgroundColor: "#f56a00" }}>
                                +{allocationValue.visiblity.length - 2}
                              </Avatar>
                            </Popover>
                          )}
                        </Avatar.Group>
                      ) : (
                        "-"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Moved On">
                      {allocationValue.created_at
                        ? moment(allocationValue.created_at).local().format("DD-MM-YYYY hh:mm A")
                        : "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Manually Cleared">
                      {allocationValue.manually_cleared ? "Yes" : "No"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Time Elapsed">
                      <span
                        style={{ color: allocationValue.accepted_on === null ? "red" : "green" }}
                      >
                        {timeElapsedArray.find((_, idx) => idx === data.indexOf(allocationValue)) || "Calculating..."}
                      </span>
                    </Descriptions.Item>

                    <Descriptions.Item label="Status">
                      {allocationValue.status || "-"}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              ))}

              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={(page, pageSize) =>
                  setPagination((prev) => ({ ...prev, current: page, pageSize }))
                }
                style={{ textAlign: "center", marginTop: 16 }}
              />
            </>
          ) : (
            <div style={{ padding: "10px" }}>
              <Empty description="No data available" />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default MobileCompAllocation